import { isGovernedReceiptProof } from './governed-receipt';
import type { GovernedReceiptProof } from './governed-receipt';
import type { RealizationBasis } from './realization-basis';

export const INITIATIVE_REALIZATION_SCHEMA_VERSION = 'initiative_realization_v1';
export const INITIATIVE_REALIZATION_CATEGORIES = ['measured_operational_change', 'modeled_opportunity', 'attributable_realized_operating_income', 'approved_target', 'remaining_unrealized'] as const;

export type InitiativeRealizationCategory = (typeof INITIATIVE_REALIZATION_CATEGORIES)[number];
export type InitiativeRealizationValueSource = 'measured' | 'modeled' | 'attributed' | 'approved_target';
export type InitiativeRealizationUnavailableReason = 'missing_category' | 'identity_mismatch' | 'unit_mismatch' | 'source_category_mismatch' | 'non_finite_value' | 'attribution_unavailable' | 'attribution_receipt_missing' | 'attribution_evidence_missing' | 'attribution_receipt_mismatch' | 'evidence_ref_mismatch';
export type InitiativeRealizationIdentity = Readonly<{ metricKey: string; scopeKey: string; periodKey: string; basis: RealizationBasis }>;
export type InitiativeRealizationAttributionReceipt = Readonly<{ proof: GovernedReceiptProof; fieldPath: string }>;

type ValueInputBase<C extends InitiativeRealizationCategory, S extends InitiativeRealizationValueSource> = Readonly<{
  category: C; source: S; label: string; value: number; unit: string; bindingKey: string;
  identity: InitiativeRealizationIdentity; evidenceRefIds?: readonly string[];
}>;
export type InitiativeRealizationValueInput =
  | ValueInputBase<'measured_operational_change', 'measured'>
  | ValueInputBase<'modeled_opportunity', 'modeled'>
  | ValueInputBase<'approved_target', 'approved_target'>
  | (ValueInputBase<'attributable_realized_operating_income', 'attributed'> & Readonly<{ attributionReceipt: InitiativeRealizationAttributionReceipt }>);
export type InitiativeRealizationAvailable = Readonly<{
  category: InitiativeRealizationCategory; status: 'available'; label: string;
  value: number; unit: string; bindingKey: string;
  source: InitiativeRealizationValueSource | 'computed_remaining';
  identity: InitiativeRealizationIdentity; evidenceRefIds: readonly string[];
  attributionReceipt?: InitiativeRealizationAttributionReceipt;
}>;
export type InitiativeRealizationUnavailable = Readonly<{
  category: InitiativeRealizationCategory; status: 'unavailable'; label: string;
  reason: InitiativeRealizationUnavailableReason; value: null; bindingKey: null;
  identity?: InitiativeRealizationIdentity; evidenceRefIds: readonly string[];
}>;
export type InitiativeRealizationCategoryState = InitiativeRealizationAvailable | InitiativeRealizationUnavailable;
export type InitiativeRealizationSnapshot = Readonly<{
  schemaVersion: typeof INITIATIVE_REALIZATION_SCHEMA_VERSION; initiativeId: string;
  categories: Record<InitiativeRealizationCategory, InitiativeRealizationCategoryState>;
}>;

const LABEL: Record<InitiativeRealizationCategory, string> = { measured_operational_change: 'Measured operational change', modeled_opportunity: 'Modeled opportunity', attributable_realized_operating_income: 'Attributable realized operating income', approved_target: 'Approved target', remaining_unrealized: 'Remaining unrealized' };
const SOURCE: Record<InitiativeRealizationValueInput['category'], InitiativeRealizationValueSource> = { measured_operational_change: 'measured', modeled_opportunity: 'modeled', attributable_realized_operating_income: 'attributed', approved_target: 'approved_target' };

export function compatibleInitiativeRealizationIdentity(left?: InitiativeRealizationIdentity, right?: InitiativeRealizationIdentity): boolean {
  return !!left && !!right && left.metricKey === right.metricKey && left.scopeKey === right.scopeKey &&
    left.periodKey === right.periodKey && left.basis === right.basis;
}

export function buildInitiativeRealizationSnapshot(input: {
  initiativeId: string; values: readonly InitiativeRealizationValueInput[];
  remainingBindingKey?: string; evidenceRefIds?: readonly string[];
}): InitiativeRealizationSnapshot {
  const categories = Object.fromEntries(INITIATIVE_REALIZATION_CATEGORIES.map(
    (category) => [category, unavailable(category, 'missing_category')],
  )) as Record<InitiativeRealizationCategory, InitiativeRealizationCategoryState>;
  for (const value of input.values) categories[value.category] = stateFor(value, input.evidenceRefIds ?? []);
  categories.remaining_unrealized = remaining(categories, input.remainingBindingKey);
  return { schemaVersion: INITIATIVE_REALIZATION_SCHEMA_VERSION, initiativeId: input.initiativeId, categories };
}

function stateFor(value: InitiativeRealizationValueInput, governedEvidenceRefIds: readonly string[]): InitiativeRealizationCategoryState {
  if (SOURCE[value.category] !== value.source) return unavailable(value.category, 'source_category_mismatch', value);
  if (!Number.isFinite(value.value)) return unavailable(value.category, 'non_finite_value', value);
  const evidenceRefIds = unique(value.evidenceRefIds ?? []);
  const issue = value.category === 'attributable_realized_operating_income'
    ? attributedIssue(value, governedEvidenceRefIds, evidenceRefIds)
    : null;
  return issue ? unavailable(value.category, issue, { ...value, evidenceRefIds }) : { ...value, status: 'available', evidenceRefIds };
}

function attributedIssue(
  value: Extract<InitiativeRealizationValueInput, { category: 'attributable_realized_operating_income' }>,
  governedEvidenceRefIds: readonly string[],
  evidenceRefIds: readonly string[],
): InitiativeRealizationUnavailableReason | null {
  if (evidenceRefIds.length === 0) return 'attribution_evidence_missing';
  const receipt = (value as { attributionReceipt?: InitiativeRealizationAttributionReceipt }).attributionReceipt;
  if (!receipt?.fieldPath.trim() || !isGovernedReceiptProof(receipt.proof)) return 'attribution_receipt_missing';
  if (!evidenceRefIds.every((refId) => governedEvidenceRefIds.includes(refId))) return 'evidence_ref_mismatch';
  const binding = receipt.proof.valueBindings.filter((row) => row.fieldPath === receipt.fieldPath);
  return binding.length === 1 && receiptMatches(binding[0], value) && identityMatches(receipt.proof.identity, value.identity)
    ? null
    : 'attribution_receipt_mismatch';
}

function receiptMatches(binding: GovernedReceiptProof['valueBindings'][number], value: InitiativeRealizationValueInput): boolean {
  return binding.value === value.value && binding.unit === value.unit &&
    binding.scope === value.identity.scopeKey && binding.timeframe === value.identity.periodKey;
}

function identityMatches(receiptIdentity: Readonly<Record<string, string>>, expected: InitiativeRealizationIdentity): boolean {
  return receiptIdentity.metricKey === expected.metricKey && receiptIdentity.scopeKey === expected.scopeKey &&
    receiptIdentity.periodKey === expected.periodKey && receiptIdentity.basis === expected.basis;
}

function remaining(categories: Record<InitiativeRealizationCategory, InitiativeRealizationCategoryState>, bindingKey = 'remaining_unrealized'): InitiativeRealizationCategoryState {
  const realized = categories.attributable_realized_operating_income;
  const target = categories.approved_target;
  if (realized.status !== 'available') return unavailable('remaining_unrealized', realized.reason);
  if (target.status !== 'available') return unavailable('remaining_unrealized', target.reason);
  const evidenceRefIds = unique([...realized.evidenceRefIds, ...target.evidenceRefIds]);
  if (realized.unit !== target.unit) return unavailable('remaining_unrealized', 'unit_mismatch', { evidenceRefIds });
  if (!compatibleInitiativeRealizationIdentity(realized.identity, target.identity)) return unavailable('remaining_unrealized', 'identity_mismatch', { evidenceRefIds });
  return {
    category: 'remaining_unrealized', status: 'available', label: LABEL.remaining_unrealized,
    value: Math.round((target.value - realized.value) * 100) / 100, unit: target.unit, bindingKey,
    source: 'computed_remaining', identity: target.identity, evidenceRefIds,
    attributionReceipt: realized.attributionReceipt,
  };
}

function unavailable(
  category: InitiativeRealizationCategory,
  reason: InitiativeRealizationUnavailableReason,
  value?: { label?: string; identity?: InitiativeRealizationIdentity; evidenceRefIds?: readonly string[] },
): InitiativeRealizationUnavailable {
  return {
    category, status: 'unavailable', label: value?.label ?? LABEL[category],
    reason, value: null, bindingKey: null, identity: value?.identity,
    evidenceRefIds: value?.evidenceRefIds ?? [],
  };
}

function unique(values: readonly string[]): readonly string[] {
  return [...new Set(values.map((value) => value.trim()).filter(Boolean))].sort((left, right) => left.localeCompare(right));
}
